import cn from 'classnames';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArticleEditorPageRedirect } from '@widgets/articles/article-edit/helpers.ts';
import { useArticleRecommend } from '@widgets/articles/RecommendModal/model.ts';
import { useAppDispatch, useUser } from '@shared/hooks/index.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import * as st from '@shared/styles/smallCardMenu.module.scss';
import {
  DropdownItem,
  DropdownMenuWithButton,
  useMenuDropdown,
} from '@shared/ui/menus/dropdown-menu';
import { useDeleteArticleFromFavoriteMutation } from '@src/api/api-hooks/articles/useDeleteArticleFromFavoriteMutation.ts';
import { useDeleteArticleMutation } from '@src/api/api-hooks/articles/useDeleteArticleMutation.ts';
import { useMakeArticleFavoriteMutation } from '@src/api/api-hooks/articles/useMakeArticleFavoriteMutation.ts';
import { openDialogModal } from '@src/library/redux/modal/index.ts';

const showDeleteModal = ({ onRemove }: { onRemove: () => void }) =>
  openDialogModal({
    isNeedClose: true,
    title: 'Удаление статьи',
    text: 'Вы действительно хотите удалить статью?',
    confirmText: 'Удалить',
    confirm: onRemove,
    declineText: 'Оставить',
  });

type PT = {
  id: number;
  isFavorite: boolean;
  isAuthor: boolean;
  className?: string;
  needDropDawnShowUp?: boolean;
  authorId?: number;
  isLargeCardDropdown?: boolean;
};

export const ArticleDropMenu = memo(
  ({
    id,
    isAuthor,
    className,
    isFavorite,
    needDropDawnShowUp,
    authorId,
    isLargeCardDropdown,
  }: PT) => {
    const dispatch = useAppDispatch();
    const { code } = useModuleByCodeParam();
    const { menuRef, handleToggleDropdown, isMenuOpen, handleAction } = useMenuDropdown({});
    const { mutateAsync: deleteArticle } = useDeleteArticleMutation();
    const { isAuth } = useUser();
    const { mutateAsync: makeArticleFavorite } = useMakeArticleFavoriteMutation();
    const { mutateAsync: deleteArticleFromFavorite } = useDeleteArticleFromFavoriteMutation();
    const nav = useNavigate();
    const { openModal } = useArticleRecommend();
    const { doRedirectWithSavePageUrl } = useArticleEditorPageRedirect();
    return (
      <DropdownMenuWithButton
        isMenuOpen={isMenuOpen}
        onMenuBtnClick={handleToggleDropdown}
        menuRef={menuRef}
        wrapperClassName={className}
        dropDownClassName={cn(
          needDropDawnShowUp ? st.lastMenu : undefined,
          needDropDawnShowUp && !isAuthor ? st.lastMenuWhenIsNotAuthor : undefined
        )}
        mouseLeaveAreaClassName={cn(needDropDawnShowUp ? st.lastMenuMouseLeaveArea : undefined)}
      >
        {isAuthor && (
          <DropdownItem onClick={handleAction(() => nav(`/articles/${code}/${id}/publication`))}>
            Публикация
          </DropdownItem>
        )}
        {!isAuthor && isAuth && (
          <DropdownItem
            onClick={handleAction(async () => {
              isFavorite
                ? await deleteArticleFromFavorite({ id: String(id) })
                : await makeArticleFavorite({ id: String(id) });
            })}
          >
            {isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
          </DropdownItem>
        )}

        <DropdownItem onClick={handleAction(() => openModal({ id, code }))}>
          Рекомендовать
        </DropdownItem>

        {!isAuthor && authorId && (
          <DropdownItem onClick={handleAction(() => nav(`/coaches/specialists/${authorId}`))}>
            Об авторе
          </DropdownItem>
        )}

        {isAuthor && (
          <DropdownItem
            onClick={handleAction(() => doRedirectWithSavePageUrl(`/articles/${code}/${id}/edit`))}
          >
            Изменить
          </DropdownItem>
        )}
        {isAuthor && (
          <DropdownItem
            onClick={handleAction(() =>
              dispatch(
                showDeleteModal({
                  onRemove: async () => {
                    await deleteArticle({ id: String(id) });
                    isLargeCardDropdown && nav(-1);
                  },
                })
              )
            )}
          >
            Удалить
          </DropdownItem>
        )}
      </DropdownMenuWithButton>
    );
  }
);
